import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Map, X, Minus, Maximize2, Minimize2,
  PanelLeftClose, PanelLeft, Plus, Search, Trash2, GripHorizontal
} from 'lucide-react';
import { operationalAssistantService } from '../../services/erpApi';
import OperationalAssistantMessageList from './OperationalAssistantMessageList';
import SellerAssistantInput from '../SellerAssistant/SellerAssistantInput';
import {
  useDraggablePanel,
  PANEL_WIDTH,
  PANEL_WIDTH_WIDE,
  PANEL_HEIGHT,
  PANEL_HEIGHT_MIN,
} from '../SellerAssistant/useDraggablePanel';
import '../SellerAssistant/SellerAssistantPanel.css';
import './OperationalAssistantPanel.css';
import './OperationalAssistantMessageList.css';

const SUGGESTIONS = [
  'Como cadastro um cliente?',
  'Como criar um produto?',
  'Como faço um pedido de compras?',
];

function formatConvDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function OperationalAssistantPanel({ open = true, onClose, variant = 'panel' }) {
  const isPage = variant === 'page';
  const [isMinimized, setIsMinimized] = useState(false);
  const [isWide, setIsWide] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [waitingResponse, setWaitingResponse] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const panelWidth = isWide && !isPage ? PANEL_WIDTH_WIDE : PANEL_WIDTH;
  const panelHeight = isMinimized && !isPage ? PANEL_HEIGHT_MIN : PANEL_HEIGHT;

  const { position, isDragging, handleDragStart } = useDraggablePanel({
    enabled: !isPage && open,
    width: panelWidth,
    height: panelHeight,
  });

  useEffect(() => {
    if (open && !isPage) {
      setIsMinimized(false);
      setIsWide(true);
    }
  }, [open, isPage]);

  const panelStyle = useMemo(() => {
    if (isPage) return undefined;
    return {
      left: `${position.x}px`,
      top: `${position.y}px`,
      width: `${panelWidth}px`,
      height: `${panelHeight}px`,
    };
  }, [isPage, position.x, position.y, panelWidth, panelHeight]);

  const loadConversations = useCallback(async () => {
    try {
      setLoadingConversations(true);
      const response = await operationalAssistantService.getConversations();
      if (response.success) {
        setConversations(response.data || []);
      }
    } catch (err) {
      console.error('Erro ao carregar conversas operacionais:', err);
      setError('Não foi possível carregar suas conversas.');
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    try {
      setLoadingMessages(true);
      setError(null);
      const response = await operationalAssistantService.getConversation(conversationId);
      if (response.success) {
        setMessages(response.data?.messages || []);
      }
    } catch (err) {
      console.error('Erro ao carregar mensagens operacionais:', err);
      setError('Não foi possível carregar a conversa.');
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (selectedConversationId) {
      loadMessages(selectedConversationId);
    } else {
      setMessages([]);
    }
  }, [selectedConversationId, loadMessages]);

  const handleNewConversation = async () => {
    try {
      setError(null);
      const response = await operationalAssistantService.createConversation();
      if (response.success) {
        const created = response.data;
        await loadConversations();
        setSelectedConversationId(created.id);
        setMessages([]);
        setShowSidebar(false);
      }
    } catch (err) {
      console.error('Erro ao criar conversa operacional:', err);
      setError('Não foi possível iniciar nova conversa.');
    }
  };

  const handleSelectConversation = (id) => {
    setSelectedConversationId(id);
    setShowSidebar(false);
    setError(null);
  };

  const handleDeleteConversation = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Excluir esta conversa?')) return;
    try {
      await operationalAssistantService.deleteConversation(id);
      if (selectedConversationId === id) {
        setSelectedConversationId(null);
        setMessages([]);
      }
      await loadConversations();
    } catch (err) {
      console.error('Erro ao excluir conversa operacional:', err);
      setError('Não foi possível excluir a conversa.');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = inputValue.trim();
    if (!text || sending) return;

    setSending(true);
    setWaitingResponse(true);
    setError(null);
    setInputValue('');

    const optimisticUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticUserMsg]);

    try {
      let response;

      if (!selectedConversationId) {
        response = await operationalAssistantService.chat(text);
        if (response.success) {
          const { conversationId, message } = response.data;
          setSelectedConversationId(conversationId);
          await loadConversations();
          setMessages((prev) => {
            const withoutTemp = prev.filter((m) => m.id !== optimisticUserMsg.id);
            return [
              ...withoutTemp,
              { role: 'user', content: text, created_at: new Date().toISOString() },
              {
                id: message.id,
                role: 'assistant',
                content: message.content,
                created_at: message.createdAt,
              },
            ];
          });
        }
      } else {
        response = await operationalAssistantService.sendMessage(selectedConversationId, text);
        if (response.success) {
          const { message } = response.data;
          setMessages((prev) => {
            const withoutTemp = prev.filter((m) => m.id !== optimisticUserMsg.id);
            return [
              ...withoutTemp,
              { role: 'user', content: text, created_at: new Date().toISOString() },
              {
                id: message.id,
                role: 'assistant',
                content: message.content,
                created_at: message.createdAt,
              },
            ];
          });
          await loadConversations();
        }
      }

      if (!response?.success) {
        setError(response?.message || 'Erro ao obter resposta do assistente.');
        setMessages((prev) => prev.filter((m) => m.id !== optimisticUserMsg.id));
      }
    } catch (err) {
      console.error('Erro ao enviar mensagem operacional:', err);
      const msg = err.response?.data?.message || err.message || 'Assistente indisponível.';
      setError(msg);
      setMessages((prev) => prev.filter((m) => m.id !== optimisticUserMsg.id));
    } finally {
      setSending(false);
      setWaitingResponse(false);
    }
  };

  const handleSuggestion = (text) => {
    setInputValue(text);
  };

  const filteredConversations = conversations.filter((conv) => {
    if (!searchTerm.trim()) return true;
    return (conv.title || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const activeConv = conversations.find((c) => c.id === selectedConversationId);

  const containerClass = [
    'seller-assistant-container',
    'operational-assistant-theme',
    isPage ? 'seller-assistant-page' : 'seller-assistant-floating',
    !open && !isPage ? 'seller-assistant-hidden' : '',
    isMinimized && !isPage ? 'seller-assistant-minimized' : '',
    isWide && !isPage ? 'seller-assistant-wide' : '',
    isDragging ? 'seller-assistant-dragging' : '',
  ].filter(Boolean).join(' ');

  if (!open && !isPage) {
    return (
      <div className={containerClass} style={panelStyle} aria-hidden="true">
        <div className="seller-assistant-main" />
      </div>
    );
  }

  return (
    <div className={containerClass} style={panelStyle}>
      <div className="seller-assistant-main">
        <div className={`seller-assistant-sidebar ${showSidebar ? 'seller-assistant-sidebar-open' : ''}`}>
          <div className="seller-assistant-sidebar-header">
            <button type="button" className="seller-assistant-new-btn" onClick={handleNewConversation}>
              <Plus size={14} />
              Nova conversa
            </button>
            <div className="seller-assistant-search-wrapper">
              <Search size={14} className="seller-assistant-search-icon" />
              <input
                type="text"
                placeholder="Buscar conversa..."
                className="seller-assistant-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="seller-assistant-sidebar-content">
            {loadingConversations ? (
              <div className="seller-assistant-sidebar-empty">Carregando...</div>
            ) : filteredConversations.length === 0 ? (
              <div className="seller-assistant-sidebar-empty">Nenhuma conversa ainda</div>
            ) : (
              filteredConversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`seller-assistant-conv-item ${
                    selectedConversationId === conv.id ? 'seller-assistant-conv-item-active' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleSelectConversation(conv.id)}
                    className="seller-assistant-conv-select"
                  >
                    <div className="seller-assistant-conv-info">
                      <p className="seller-assistant-conv-title">{conv.title || 'Nova conversa'}</p>
                      <p className="seller-assistant-conv-date">{formatConvDate(conv.updated_at)}</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    className="seller-assistant-conv-delete"
                    onClick={(e) => handleDeleteConversation(e, conv.id)}
                    aria-label="Excluir conversa"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="seller-assistant-area">
          <div
            className="seller-assistant-header"
            onMouseDown={!isPage ? handleDragStart : undefined}
            role={!isPage ? 'presentation' : undefined}
          >
            <div className="seller-assistant-header-left">
              {!isPage && (
                <span className="seller-assistant-drag-handle" title="Arrastar painel">
                  <GripHorizontal size={16} />
                </span>
              )}
              <button
                type="button"
                onClick={() => setShowSidebar(!showSidebar)}
                className={`seller-assistant-header-button ${showSidebar ? 'seller-assistant-header-button-active' : ''}`}
                title="Histórico de conversas"
              >
                {showSidebar ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
              </button>
              <div className="seller-assistant-header-brand">
                <div className="seller-assistant-header-icon">
                  <Map size={18} />
                </div>
                <div>
                  <h4 className="seller-assistant-header-title">Assistente Operacional</h4>
                  <p className="seller-assistant-header-subtitle">
                    {activeConv?.title || 'Como usar o ERP'}
                  </p>
                </div>
              </div>
            </div>

            <div className="seller-assistant-header-actions">
              {!isPage && !isMinimized && (
                <button
                  type="button"
                  onClick={() => setIsWide(!isWide)}
                  className="seller-assistant-header-action"
                  aria-label={isWide ? 'Reduzir painel' : 'Expandir painel'}
                >
                  {isWide ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
              )}
              {!isPage && (
                <button
                  type="button"
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="seller-assistant-header-action"
                  aria-label="Minimizar"
                >
                  <Minus size={18} />
                </button>
              )}
              {!isPage && onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="seller-assistant-header-action seller-assistant-header-action-close"
                  aria-label="Fechar"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {!isMinimized && (
            <>
              {error && (
                <div className="seller-assistant-error" role="alert">
                  {error}
                </div>
              )}

              <OperationalAssistantMessageList
                messages={messages}
                loading={loadingMessages && !!selectedConversationId}
                waitingResponse={waitingResponse}
              />

              {!selectedConversationId && messages.length === 0 && !waitingResponse && (
                <div className="seller-assistant-suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="seller-assistant-suggestion-chip"
                      onClick={() => handleSuggestion(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              <SellerAssistantInput
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onSubmit={handleSend}
                disabled={sending}
                placeholder="Como faço para..."
              />
            </>
          )}

          {isMinimized && !isPage && (
            <div className="seller-assistant-minimized-view">
              <p>Assistente minimizado — clique em expandir para continuar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OperationalAssistantPanel;
